import { useState } from 'react'
import { Button, Input, Spin, Tooltip, Modal } from 'antd'
import {
  FileTextOutlined,
  RightOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  CheckOutlined,
  StopOutlined,
} from '@ant-design/icons'
import {
  usePracticeAgentNotes,
  usePracticeAgentAddNoteMutation,
  usePracticeAgentUpdateNoteMutation,
  usePracticeAgentDeleteNoteMutation,
} from '../../hooks'
import { useToast } from '../../../../contexts/ToastContext'

const { TextArea } = Input

interface NotePanelProps {
  open: boolean
  onClose: () => void
  clientId: string
  reportBuilderId: string | undefined
  reportBuilderGroupId?: string | null
  title?: string
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function NotePanel({
  open,
  onClose,
  clientId,
  reportBuilderId,
  reportBuilderGroupId,
  title,
}: NotePanelProps) {
  const { addToast } = useToast()
  const [newNote, setNewNote] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingText, setEditingText] = useState('')

  const { data: notes, isLoading } = usePracticeAgentNotes(clientId, reportBuilderId, reportBuilderGroupId)
  const addMutation = usePracticeAgentAddNoteMutation(clientId, reportBuilderId, reportBuilderGroupId)
  const updateMutation = usePracticeAgentUpdateNoteMutation(clientId, reportBuilderId, reportBuilderGroupId)
  const deleteMutation = usePracticeAgentDeleteNoteMutation(clientId, reportBuilderId, reportBuilderGroupId)

  const contextId = reportBuilderId ?? reportBuilderGroupId

  function handleAdd() {
    const text = newNote.trim()
    if (!text || !contextId) return
    addMutation.mutate(
      {
        reportBuilderId: reportBuilderId ?? null,
        reportBuilderGroupId: reportBuilderGroupId ?? null,
        clientId,
        note: text,
      },
      {
        onSuccess: () => { setNewNote(''); addToast('Note added.', 'success') },
        onError: () => addToast('Failed to add note.', 'error'),
      },
    )
  }

  function handleAddKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleAdd()
    }
  }

  function startEdit(noteId: string, text: string) {
    setEditingId(noteId)
    setEditingText(text)
  }

  function cancelEdit() {
    setEditingId(null)
    setEditingText('')
  }

  function handleSaveEdit(noteId: string) {
    const text = editingText.trim()
    if (!text) return
    updateMutation.mutate(
      { noteId, note: text },
      {
        onSuccess: () => { cancelEdit(); addToast('Note updated.', 'success') },
        onError: () => addToast('Failed to update note.', 'error'),
      },
    )
  }

  function handleDelete(noteId: string) {
    Modal.confirm({
      title: 'Delete note?',
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: () =>
        deleteMutation.mutateAsync(noteId).then(
          () => addToast('Note deleted.', 'success'),
          () => addToast('Failed to delete note.', 'error'),
        ),
    })
  }

  return (
    <>

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-[420px] bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <FileTextOutlined className="feature-icon-accent text-lg flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-semibold text-gray-800 text-sm leading-tight truncate">Notes</p>
              {title && <p className="text-xs text-gray-400 truncate">{title}</p>}
            </div>
          </div>
          <Button
            type="text"
            size="small"
            icon={<RightOutlined />}
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 flex-shrink-0"
          />
        </div>

        {/* Notes list */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {!contextId && (
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-400 gap-3">
              <FileTextOutlined className="text-4xl text-gray-300" />
              <p className="text-sm">Open a report to view and add notes.</p>
            </div>
          )}

          {contextId && isLoading && (
            <div className="flex justify-center items-center h-32"><Spin /></div>
          )}

          {contextId && !isLoading && notes?.length === 0 && (
            <div className="flex flex-col items-center justify-center h-32 text-center text-gray-400 gap-2">
              <FileTextOutlined className="text-3xl text-gray-300" />
              <p className="text-sm">No notes yet. Add the first note below.</p>
            </div>
          )}

          {notes?.map((note) =>
            editingId === note.practiceAgentNoteId ? (
              <div key={note.practiceAgentNoteId} className="feature-panel-soft rounded-xl p-3 space-y-2">
                <TextArea
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  autoSize={{ minRows: 2, maxRows: 6 }}
                  autoFocus
                  className="text-sm"
                />
                <div className="flex justify-end gap-1.5">
                  <Button size="small" icon={<StopOutlined />} onClick={cancelEdit}>Cancel</Button>
                  <Button
                    size="small"
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={() => handleSaveEdit(note.practiceAgentNoteId)}
                    loading={updateMutation.isPending}
                    disabled={!editingText.trim()}
                    className="feature-button-solid"
                  >
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <div
                key={note.practiceAgentNoteId}
                className="border border-gray-100 rounded-xl p-3 bg-white hover:border-gray-200 transition-colors group"
              >
                <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{note.note}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-gray-400">{formatDate(note.updateDate ?? note.insertDate)}</span>
                  <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Tooltip title="Edit">
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => startEdit(note.practiceAgentNoteId, note.note)}
                        className="text-gray-400 feature-link"
                      />
                    </Tooltip>
                    <Tooltip title="Delete">
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(note.practiceAgentNoteId)}
                        loading={deleteMutation.isPending}
                        className="text-gray-400 hover:text-red-500"
                      />
                    </Tooltip>
                  </div>
                </div>
              </div>
            ),
          )}
        </div>

        {/* Add note input */}
        <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 bg-gray-50">
          {!contextId && (
            <p className="text-xs text-gray-400 mb-2 text-center">Open a single report to add notes.</p>
          )}
          <div className="flex gap-2 items-end mb-6">
            <TextArea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={handleAddKeyDown}
              placeholder="Add a note…"
              autoSize={{ minRows: 1, maxRows: 4 }}
              disabled={addMutation.isPending || !contextId}
              className="flex-1 text-sm resize-none"
            />
            <Tooltip title="Add note">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
                disabled={!newNote.trim() || addMutation.isPending || !contextId}
                loading={addMutation.isPending}
                className="feature-button-solid flex-shrink-0"
              />
            </Tooltip>
          </div>
        </div>
      </div>
    </>
  )
}
